import { toUtcDateOnlyForApi, toUtcIsoInstantForApi } from '../utils/billingDateUtils';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://tappytray.com/api';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  data: {
    token: string;
    user: {
      id: number;
      name: string;
      email: string;
      society_id?: number | null;
    };
  };
  message: string;
  error?: string;
}

export interface Promotion {
  id: number;
  title: string;
  description: string | null;
  image_url: string | null;
  video_url: string | null;
  promotion_type: 'story' | 'spotlight' | 'category';
  category_id: number | null;
  brand_name: string | null;
  discount_percentage: number | null;
  discount_amount: number | null;
  coupon_code: string | null;
  mobile_number: string | null;
  call_button_enabled: number;
  show_location: number;
  location_latitude: number | null;
  location_longitude: number | null;
  location_address: string | null;
  cover_photo_url: string | null;
  show_details_page: number;
  /** When set by admin, shows the store awning decoration on category cards. */
  show_store_awning?: number;
  action_button_required?: number;
  action_button_text?: string | null;
  action_button_link?: string | null;
  action_button_on_card?: number;
  image_urls?: string[]; // Array of additional image URLs
  society_ids?: number[]; // Array of society IDs (from mapping table)
  category_ids?: number[]; // Array of category IDs (from mapping table)
  valid_from: string;
  valid_to: string;
  is_active: number;
  display_order: number;
  click_url: string | null;
  created_at: string;
  updated_at: string;
  created_by_user_id?: number | null;
  created_by_customer_id?: number | null;
  approval_status?: 'pending_approval' | 'approved' | 'rejected';
  /** Per-promotion pricing (stat_parameter → price_per_unit). Returned by API when present. */
  stat_prices?: PromotionStatPrice[];
}

export interface PromotionStatPrice {
  id: number;
  promotion_id: number;
  stat_parameter: string;
  price_per_unit: number;
  created_at: string;
  updated_at: string;
}

export interface PromotionCategory {
  id: number;
  name: string;
  description: string | null;
  display_order: number;
  is_active: number;
}

export interface ImpressionStats {
  promotion_id: number;
  total_impressions: number;
  unique_users: number;
  unique_societies: number;
  clicks: number;
  story_views: number;
  story_completes: number;
  impressions_by_type: {
    view: number;
    click: number;
    story_view: number;
    story_complete: number;
  };
}

// Participating Events
export interface ParticipatingEvent {
  id: number;
  title: string;
  description: string | null;
  image_url: string | null;
  event_date: string | null;
  location: string | null;
  promotion_category_id: number | null;
  total_seats: number | null;
  booked_seats: number;
  is_active: number;
  display_order: number;
  button_color: string | null;
  subtext: string | null;
  price: string | null;
  button_text: string | null;
  interested_text: string | null;
  whatsapp_group_link: string | null;
  created_at: string;
  updated_at: string;
  society_ids?: number[];
}

export interface ParticipatingEventInterest {
  id: number;
  event_id: number;
  user_id: number | null;
  name: string;
  mobile: string | null;
  email: string | null;
  notes: string | null;
  created_at: string;
}

const TOKEN_KEY = 'admin_token';
const USER_TYPE_KEY = 'user_type';
const DISPLAY_NAME_KEY = 'auth_display_name';

export type UserType = 'admin' | 'customer' | 'society_admin';

// Get auth token from localStorage
export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

// Set auth token in localStorage
export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

// Remove auth token from localStorage
export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_TYPE_KEY);
  localStorage.removeItem(DISPLAY_NAME_KEY);
};

export const getUserType = (): UserType | null => {
  const t = localStorage.getItem(USER_TYPE_KEY);
  return t === 'admin' || t === 'customer' || t === 'society_admin' ? t : null;
};

export const setUserType = (userType: UserType): void => {
  localStorage.setItem(USER_TYPE_KEY, userType);
};

export const getDisplayName = (): string | null => {
  return localStorage.getItem(DISPLAY_NAME_KEY);
};

export const setDisplayName = (displayName: string): void => {
  localStorage.setItem(DISPLAY_NAME_KEY, displayName);
};

export const removeDisplayName = (): void => {
  localStorage.removeItem(DISPLAY_NAME_KEY);
};

/** Login path for 401 redirect: use current URL so customer on /customer/* goes to customer login. */
const getLoginPathForRedirect = (): string => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  if (path.startsWith('/customer')) return '/customer/login';
  if (path.startsWith('/society')) return '/society/login';
  return '/admin/login';
};

const SOCIETY_DATA_KEY = 'society_admin_data';

export interface SocietyAdminData {
  societyId: number;
  societyName: string;
  userId: number;
  userName: string;
  societies: Array<{
    society_id: number;
    society_name: string;
    userRole: string | null;
    roles: string[];
  }>;
}

export const getSocietyAdminData = (): SocietyAdminData | null => {
  const raw = localStorage.getItem(SOCIETY_DATA_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

export const setSocietyAdminData = (data: SocietyAdminData): void => {
  localStorage.setItem(SOCIETY_DATA_KEY, JSON.stringify(data));
};

export const removeSocietyAdminData = (): void => {
  localStorage.removeItem(SOCIETY_DATA_KEY);
};

// API request helper
const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      removeToken();
      window.location.href = getLoginPathForRedirect();
      throw new Error('Unauthorized');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

// Auth API
export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
  return apiRequest<LoginResponse>('/users/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
};

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
}

/** Customer self-registration (TappyTray website). Uses Customer module, not Users. */
export const register = async (data: RegisterRequest): Promise<{ success: boolean; data: { customer: { id: number; customer_name: string; email: string } }; message: string; error?: string }> => {
  const res = await fetch(`${API_BASE_URL}/customers/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
};

// Promotions API
export const getAllPromotions = async (): Promise<{ success: boolean; data: Promotion[] }> => {
  return apiRequest('/promotions/all');
};

export const getPromotionById = async (id: number): Promise<{ success: boolean; data: Promotion }> => {
  return apiRequest(`/promotions/${id}`);
};

export const createPromotion = async (promotion: Partial<Promotion>): Promise<{ success: boolean; data: Promotion }> => {
  return apiRequest('/promotions', {
    method: 'POST',
    body: JSON.stringify(promotion),
  });
};

export const updatePromotion = async (
  id: number,
  promotion: Partial<Promotion>
): Promise<{ success: boolean; data: Promotion }> => {
  return apiRequest(`/promotions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(promotion),
  });
};

export const deletePromotion = async (id: number): Promise<{ success: boolean }> => {
  return apiRequest(`/promotions/${id}`, {
    method: 'DELETE',
  });
};

/** Get pricing model for a promotion (impressions, clicks, story_views, story_completes). */
export const getPromotionStatPrices = async (promotionId: number): Promise<{ success: boolean; data: PromotionStatPrice[] }> => {
  return apiRequest(`/promotions/${promotionId}/stat-prices`);
};

/** Set pricing model for a promotion (replaces existing). */
export const setPromotionStatPrices = async (
  promotionId: number,
  prices: Array<{ stat_parameter: string; price_per_unit: number }>
): Promise<{ success: boolean; data: PromotionStatPrice[] }> => {
  return apiRequest(`/promotions/${promotionId}/stat-prices`, {
    method: 'PUT',
    body: JSON.stringify(prices),
  });
};

/** Admin only: set promotion approval status (approved | rejected). */
export const setPromotionApproval = async (id: number, status: 'approved' | 'rejected'): Promise<{ success: boolean; data: Promotion }> => {
  return apiRequest(`/promotions/${id}/approval`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
};

// Participating Events API
export const getAllParticipatingEvents = async (): Promise<{ success: boolean; data: ParticipatingEvent[] }> => {
  return apiRequest('/participating-events/all');
};

export const getParticipatingEvent = async (id: number): Promise<{ success: boolean; data: ParticipatingEvent }> => {
  return apiRequest(`/participating-events/${id}`);
};

export const createParticipatingEvent = async (data: Partial<ParticipatingEvent>): Promise<{ success: boolean; data: ParticipatingEvent }> => {
  return apiRequest('/participating-events', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateParticipatingEvent = async (id: number, data: Partial<ParticipatingEvent>): Promise<{ success: boolean; data: ParticipatingEvent }> => {
  return apiRequest(`/participating-events/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteParticipatingEvent = async (id: number): Promise<{ success: boolean }> => {
  return apiRequest(`/participating-events/${id}`, {
    method: 'DELETE',
  });
};

export const getParticipatingEventInterests = async (id: number): Promise<{ success: boolean; data: ParticipatingEventInterest[] }> => {
  return apiRequest(`/participating-events/${id}/interests`);
};

export const deleteParticipatingEventInterest = async (
  eventId: number,
  interestId: number
): Promise<{ success: boolean }> => {
  return apiRequest(`/participating-events/${eventId}/interests/${interestId}`, {
    method: 'DELETE',
  });
};

// Categories API (active only; global order — use for promotion forms)
export const getCategories = async (): Promise<{ success: boolean; data: PromotionCategory[] }> => {
  return apiRequest('/promotions/categories');
};

/** Admin: all promotion categories including inactive (Category Modules page). */
export const getPromotionCategoriesAdmin = async (): Promise<{ success: boolean; data: PromotionCategory[] }> => {
  return apiRequest('/promotions/admin/categories');
};

export interface UpdatePromotionCategoryBody {
  name?: string;
  description?: string | null;
  display_order?: number;
  is_active?: number;
}

export const updatePromotionCategory = async (
  id: number,
  body: UpdatePromotionCategoryBody
): Promise<{ success: boolean; data: PromotionCategory }> => {
  return apiRequest(`/promotions/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

/** Per-society home category order (active categories). */
export interface SocietyPromotionCategoryRow extends PromotionCategory {
  global_display_order?: number;
}

export const getSocietyPromotionCategoryOrder = async (
  societyId: number
): Promise<{ success: boolean; data: SocietyPromotionCategoryRow[] }> => {
  return apiRequest(`/societies/admin/${societyId}/promotion-category-order`);
};

export const saveSocietyPromotionCategoryOrder = async (
  societyId: number,
  orders: Array<{ promotion_category_id: number; display_order: number }>
): Promise<{ success: boolean; message?: string }> => {
  return apiRequest(`/societies/admin/${societyId}/promotion-category-order`, {
    method: 'PUT',
    body: JSON.stringify({ orders }),
  });
};

// Societies API
export interface SocietySummary {
  id: number;
  name: string;
  location?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  latlong?: string | null;
  created_at?: string;
  flat_count?: number;
  member_count?: number;
}

export const getSocieties = async (): Promise<{ success: boolean; data: SocietySummary[] }> => {
  return apiRequest('/societies/all');
};

export interface AdminSocietyFlatUser {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  is_primary: boolean;
  approval_status: string;
  mapped_at: string;
  role_in_flat?: string | null;
}

export interface AdminSocietyFlat {
  id: number;
  flat_number: string;
  floor: number;
  area: number | null;
  flat_type?: string | null;
  block_name: string | null;
  users: AdminSocietyFlatUser[];
}

export interface AdminSocietyActivityLog {
  occurred_at: string;
  event_type: string;
  title: string;
  detail: string | null;
  actor_name: string | null;
}

export interface AdminSocietyDetailSociety {
  id: number;
  name: string;
  location: string;
  city: string;
  state: string | null;
  pincode: string | number | null;
  latlong: string | null;
  country: string | null;
  type: string | null;
  /** Invitation / join passcode for residents */
  society_passcode?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminSocietyDetail {
  society: AdminSocietyDetailSociety;
  flats: AdminSocietyFlat[];
  activity_logs: AdminSocietyActivityLog[];
}

export const getAdminSocietyDetail = async (
  societyId: number
): Promise<{ success: boolean; data: AdminSocietyDetail }> => {
  return apiRequest(`/societies/admin/${societyId}/detail`);
};

export interface AdminSocietyFlatDetail {
  society: { id: number; name: string };
  flat: AdminSocietyFlat;
  flat_activity_logs: AdminSocietyActivityLog[];
  per_user_activity: Array<{
    user_id: number;
    user_name: string;
    activity_logs: AdminSocietyActivityLog[];
  }>;
}

export const getAdminSocietyFlatDetail = async (
  societyId: number,
  flatId: number
): Promise<{ success: boolean; data: AdminSocietyFlatDetail }> => {
  return apiRequest(`/societies/admin/${societyId}/flats/${flatId}/detail`);
};

export const updateFlat = async (
  flatId: number,
  payload: { flat_type?: string | null; area?: number; flatNumber?: string }
): Promise<{ success: boolean; data: AdminSocietyFlat; message?: string }> => {
  return apiRequest(`/flats/${flatId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export interface FlatMergeMemberPreview {
  userId: number;
  name: string;
  phone: string | null;
  role: string;
  vehicles: string[];
}

export interface FlatMergeFlatPreview {
  id: number;
  flatNumber: string;
  memberCount: number;
  members: FlatMergeMemberPreview[];
}

export interface FlatMergeActionPreview {
  userId: number;
  userName: string;
  sourceRole: string;
  action: string;
  targetRole?: string;
  vehicles: string[];
}

export interface FlatDuplicateGroupPreview {
  groupKey: string;
  canonical: string;
  blockId: number;
  blockName: string;
  master: FlatMergeFlatPreview;
  corrupted: FlatMergeFlatPreview[];
  actions: FlatMergeActionPreview[];
  warnings: string[];
}

export interface FlatMergeResult {
  groupKey: string;
  canonical: string;
  masterFlatId: number;
  mergedFlatIds: number[];
  deletedFlatIds: number[];
  membersMoved: number;
  vehiclesMoved: number;
}

export const getFlatDuplicatePreview = async (
  societyId: number
): Promise<{ success: boolean; data: FlatDuplicateGroupPreview[]; message?: string }> => {
  return apiRequest(`/societies/admin/${societyId}/flat-duplicates`);
};

export const mergeFlatDuplicateGroup = async (
  societyId: number,
  masterFlatId: number
): Promise<{ success: boolean; data: FlatMergeResult; message?: string }> => {
  return apiRequest(`/societies/admin/${societyId}/flat-duplicates/merge`, {
    method: 'POST',
    body: JSON.stringify({ masterFlatId }),
  });
};

export interface FlatMergeAllResult {
  mergedGroups: FlatMergeResult[];
  totalGroups: number;
  totalDuplicatesRemoved: number;
}

export const mergeAllFlatDuplicateGroups = async (
  societyId: number
): Promise<{ success: boolean; data: FlatMergeAllResult; message?: string }> => {
  return apiRequest(`/societies/admin/${societyId}/flat-duplicates/merge-all`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
};

export interface CompoundSplitTargetPreview {
  canonical: string;
  masterFlatId: number | null;
  masterFlatNumber: string | null;
  found: boolean;
}

export interface CompoundFlatPreview {
  compoundFlatId: number;
  compoundFlatNumber: string;
  blockId: number;
  blockName: string;
  parsedUnits: string[];
  targets: CompoundSplitTargetPreview[];
  members: Array<{
    userFlatId: number;
    userId: number;
    name: string;
    phone: string | null;
    role: string;
    vehicles: string[];
  }>;
  actions: Array<{
    userId: number;
    userName: string;
    sourceRole: string;
    targetCanonical: string;
    action: string;
    targetRole?: string;
  }>;
  warnings: string[];
}

export interface CompoundSplitResult {
  compoundFlatId: number;
  compoundFlatNumber: string;
  targetFlatIds: number[];
  targetCanonicals: string[];
  membersAssigned: number;
  vehiclesMoved: number;
  deletedCompoundFlat: boolean;
}

export interface CompoundSplitAllResult {
  results: CompoundSplitResult[];
  totalCompoundFlats: number;
  totalDeleted: number;
}

export const getCompoundFlatPreview = async (
  societyId: number
): Promise<{ success: boolean; data: CompoundFlatPreview[]; message?: string }> => {
  return apiRequest(`/societies/admin/${societyId}/flat-compounds`);
};

export const splitCompoundFlat = async (
  societyId: number,
  compoundFlatId: number
): Promise<{ success: boolean; data: CompoundSplitResult; message?: string }> => {
  return apiRequest(`/societies/admin/${societyId}/flat-compounds/split`, {
    method: 'POST',
    body: JSON.stringify({ compoundFlatId }),
  });
};

export const splitAllCompoundFlats = async (
  societyId: number
): Promise<{ success: boolean; data: CompoundSplitAllResult; message?: string }> => {
  return apiRequest(`/societies/admin/${societyId}/flat-compounds/split-all`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
};

// Category API
export interface CreateCategoryRequest {
  name: string;
  description?: string;
  display_order?: number;
}

export const createCategory = async (data: CreateCategoryRequest): Promise<{ success: boolean; data: PromotionCategory }> => {
  return apiRequest('/promotions/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Statistics API
export const getAllPromotionStats = async (): Promise<{ success: boolean; data: ImpressionStats[] }> => {
  return apiRequest('/promotions/stats/all');
};

/** Customer: all promotion stats for logged-in customer (GET /customers/me/promotion-stats). Use this for Stats page to avoid 401. */
export const getMyPromotionStats = async (): Promise<{ success: boolean; data: ImpressionStats[]; message?: string }> => {
  return apiRequest('/customers/me/promotion-stats');
};

/** Get stats for a single promotion (admin: any; customer: own only). Uses GET /promotions/:promotionId/stats; customerId from token. */
export const getPromotionStats = async (id: number): Promise<{ success: boolean; data: ImpressionStats }> => {
  return apiRequest(`/promotions/${id}/stats`);
};

// Customer: get my promotions only
export const getMyPromotions = async (): Promise<{ success: boolean; data: Promotion[] }> => {
  return apiRequest('/promotions/my');
};

// Society Overview API
export interface SocietyPromotionOverview {
  society_id: number;
  society_name: string;
  promotion_id: number;
  promotion_title: string;
  promotion_type: string;
  brand_name: string | null;
  discount_percentage: number | null;
  discount_amount: number | null;
  coupon_code: string | null;
  valid_from: string;
  valid_to: string;
  is_active: number;
}

export const getSocietyWiseOverview = async (): Promise<{ success: boolean; data: SocietyPromotionOverview[] }> => {
  return apiRequest('/promotions/society-overview');
};

// Document Upload API
export interface UploadDocumentResponse {
  success: boolean;
  data: {
    fileId: string;
    filePath: string;
    fileName: string;
    fileSize: number;
    contentType: string;
    uploadedAt: string;
    publicUrl?: string; // Public URL if file is made public (for promotion images)
  };
  message: string;
}

export interface DocumentUrlResponse {
  success: boolean;
  data: {
    url: string;
    filePath: string;
    expiresIn: number;
  };
}

export const uploadDocument = async (file: File, societyId: number): Promise<UploadDocumentResponse> => {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('societyId', societyId.toString());

  const response = await fetch(`${API_BASE_URL}/documents/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 401) {
      removeToken();
      window.location.href = getLoginPathForRedirect();
      throw new Error('Unauthorized');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

export const uploadPromotionImage = async (file: File): Promise<UploadDocumentResponse> => {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/documents/promotion/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 401) {
      removeToken();
      window.location.href = getLoginPathForRedirect();
      throw new Error('Unauthorized');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

export const getDocumentUrl = async (filePath: string, expiresIn: number = 60): Promise<DocumentUrlResponse> => {
  const residentToken = localStorage.getItem(RESIDENT_TOKEN_KEY);
  const token = residentToken || getToken();
  const response = await fetch(`${API_BASE_URL}/documents/url?filePath=${encodeURIComponent(filePath)}&expiresIn=${expiresIn || 200000}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      if (residentToken) {
        localStorage.removeItem(RESIDENT_TOKEN_KEY);
        localStorage.removeItem(RESIDENT_CTX_KEY);
      } else {
        removeToken();
        window.location.href = getLoginPathForRedirect();
      }
      throw new Error('Unauthorized');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  return response.json();
};

/** GCS URL without v2/v4 signature query params — bucket is private, so these 403 in the browser. */
export function isUnsignedGcsObjectUrl(url: string): boolean {
  if (!url.includes('storage.googleapis.com')) return false;
  return !url.includes('X-Goog-Algorithm=') && !url.includes('GoogleAccessId=');
}

export function revokeBlobUrl(url: string | null | undefined): void {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

/** Stream document through API (works when signed URLs are unavailable). */
export const fetchDocumentBlobUrl = async (filePath: string): Promise<string> => {
  const residentToken = localStorage.getItem(RESIDENT_TOKEN_KEY);
  const token = residentToken || getToken();
  if (!token) {
    throw new Error('Unauthorized');
  }
  const response = await fetch(
    `${API_BASE_URL}/documents/download?filePath=${encodeURIComponent(filePath)}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response.ok) {
    if (response.status === 401) {
      if (residentToken) {
        localStorage.removeItem(RESIDENT_TOKEN_KEY);
        localStorage.removeItem(RESIDENT_CTX_KEY);
      } else {
        removeToken();
        window.location.href = getLoginPathForRedirect();
      }
      throw new Error('Unauthorized');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  return URL.createObjectURL(await response.blob());
};

/**
 * URL suitable for <img src> — uses signed GCS URL when valid, otherwise authenticated API proxy.
 */
export const resolveDocumentImageUrl = async (filePath: string): Promise<string> => {
  try {
    const doc = await getDocumentUrl(filePath);
    if (doc.data?.url && !isUnsignedGcsObjectUrl(doc.data.url)) {
      return doc.data.url;
    }
  } catch {
    /* fall through to proxy */
  }
  return fetchDocumentBlobUrl(filePath);
};

// --- Customer (TappyTray website) ---
export interface CustomerRecord {
  id: number;
  customer_name: string;
  business_name: string | null;
  email: string;
  address: string | null;
  location: string | null;
  city: string | null;
  state: string | null;
  pin_code: string | null;
  credits: number;
  created_at: string;
  updated_at: string;
}

export interface CustomerLoginResponse {
  success: boolean;
  data: { token: string; customer: CustomerRecord };
  message: string;
  error?: string;
}

export const customerLogin = async (credentials: LoginRequest): Promise<CustomerLoginResponse> => {
  return apiRequest<CustomerLoginResponse>('/customers/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
};

export const getCustomers = async (): Promise<{ success: boolean; data: CustomerRecord[] }> => {
  return apiRequest('/customers');
};

export interface CreateCustomerRequest {
  customer_name: string;
  business_name?: string;
  email: string;
  password: string;
  address?: string;
  location?: string;
  city?: string;
  state?: string;
  pin_code?: string;
  credits?: number;
  society_ids?: number[];
}

export const createCustomer = async (data: CreateCustomerRequest): Promise<{ success: boolean; data: CustomerRecord }> => {
  return apiRequest('/customers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const getCustomerById = async (id: number): Promise<{ success: boolean; data: CustomerRecord }> => {
  return apiRequest(`/customers/${id}`);
};

export interface UpdateCustomerRequest {
  customer_name?: string;
  business_name?: string;
  email?: string;
  password?: string;
  address?: string;
  location?: string;
  city?: string;
  state?: string;
  pin_code?: string;
  credits?: number;
  society_ids?: number[];
}

export const updateCustomer = async (id: number, data: UpdateCustomerRequest): Promise<{ success: boolean; data: CustomerRecord }> => {
  return apiRequest(`/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const getCustomerSocieties = async (customerId: number): Promise<{ success: boolean; data: Array<{ id: number; name: string }> }> => {
  return apiRequest(`/customers/${customerId}/societies`);
};

export const addCustomerCredits = async (customerId: number, amount: number, notes?: string): Promise<{ success: boolean; data: CustomerRecord }> => {
  return apiRequest(`/customers/${customerId}/credits`, {
    method: 'POST',
    body: JSON.stringify({ amount, notes }),
  });
};

export interface CustomerCreditTransaction {
  id: number;
  customer_id: number;
  amount: number;
  type: string;
  reference_id: number | null;
  admin_user_id: number | null;
  notes: string | null;
  promotion_id?: number | null;
  stat_parameter?: string | null;
  price_per_unit?: number | null;
  created_at: string;
}

export const getCustomerCreditTransactions = async (customerId: number): Promise<{ success: boolean; data: CustomerCreditTransaction[] }> => {
  return apiRequest(`/customers/${customerId}/credit-transactions`);
};

export interface CustomerCreditRequest {
  id: number;
  customer_id: number;
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  admin_user_id: number | null;
  reviewed_at: string | null;
  created_at: string;
}

export const getCreditRequests = async (): Promise<{ success: boolean; data: CustomerCreditRequest[] }> => {
  return apiRequest('/customers/credit-requests');
};

export const approveCreditRequest = async (id: number, approve: boolean): Promise<{ success: boolean; data: CustomerRecord }> => {
  return apiRequest(`/customers/credit-requests/${id}/approve`, {
    method: 'PATCH',
    body: JSON.stringify({ approve }),
  });
};

export const getMe = async (): Promise<{ success: boolean; data: CustomerRecord }> => {
  return apiRequest('/customers/me/profile');
};

export const getMySocieties = async (): Promise<{ success: boolean; data: Array<{ id: number; name: string }> }> => {
  return apiRequest('/customers/me/societies');
};

export const getMyCreditTransactions = async (): Promise<{ success: boolean; data: CustomerCreditTransaction[] }> => {
  return apiRequest('/customers/me/credit-transactions');
};

export const createCreditRequest = async (amount: number): Promise<{ success: boolean; data: CustomerCreditRequest }> => {
  return apiRequest('/customers/me/credit-requests', {
    method: 'POST',
    body: JSON.stringify({ amount }),
  });
};

export const getMyCreditRequests = async (): Promise<{ success: boolean; data: CustomerCreditRequest[] }> => {
  return apiRequest('/customers/me/credit-requests');
};

// ---------- OTP Auth (Society Admin) ----------

export interface OtpInitiateResponse {
  success: boolean;
  data: { userExists: boolean };
  message: string;
}

export interface OtpVerifyResponse {
  success: boolean;
  data: {
    token: string;
    refreshToken: string;
    user: {
      id: number;
      name: string;
      phone: string;
      email: string | null;
      role: string | null;
      society: { society_id: number; society_name: string } | null;
      societies: Array<{
        society_id: number;
        society_name: string;
        userRole: string | null;
        roles: string[];
      }>;
      flats: Array<{
        flat_id: number;
        society_id: number;
        flat_number: string;
        society_name: string;
      }>;
    };
  };
  message: string;
  error?: string;
}

export const otpInitiate = async (phone: string): Promise<OtpInitiateResponse> => {
  const res = await fetch(`${API_BASE_URL}/otp/initiate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
};

export const otpVerify = async (phone: string, otp: string): Promise<OtpVerifyResponse> => {
  const res = await fetch(`${API_BASE_URL}/otp/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, otp }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
};

export const otpResend = async (phone: string): Promise<{ success: boolean; message: string }> => {
  const res = await fetch(`${API_BASE_URL}/otp/resend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
};

// ---------- Reconciliation API ----------

export interface PendingPaymentForReconciliation {
  paymentId: number;
  paymentIds: number[];
  billId: number;
  flatId: number;
  flatNumber: string;
  blockName: string | null;
  totalAmount: number;
  amount: number;
  billingDate: string | null;
  billType: string;
  status: string;
  transactionId: string | null;
  paymentMode: string | null;
  screenshotFilePath: string | null;
  screenshotFileId: string | null;
  residentName: string | null;
  remarks: string | null;
  paidAt: string;
}

/** @deprecated Alias — billId equals paymentId for legacy reconcile code paths. */
export type PendingBillForReconciliation = PendingPaymentForReconciliation;

export interface BankTransaction {
  id: string;
  date: string;
  particulars: string;
  debit: number | null;
  credit: number | null;
  balance: number | null;
  transactionRefs: string[];
  paymentMode: string | null;
  rawText: string;
}

export interface ReconciliationMatch {
  paymentId: number;
  paymentIds: number[];
  billId: number;
  flatNumber: string;
  blockName: string | null;
  billAmount: number;
  billingDate: string;
  billTransactionId: string;
  paymentMode: string | null;
  screenshotFilePath: string | null;
  residentName: string | null;
  bankTransactionId: string;
  bankDate: string;
  bankAmount: number;
  bankParticulars: string;
  bankPaymentMode: string | null;
  matchConfidence: 'high' | 'medium' | 'low';
  matchReason: string;
  billType: string | null;
  remarks: string | null;
  needsReview?: boolean;
}

export interface ReconciliationResult {
  matched: ReconciliationMatch[];
  unmatchedBills: PendingBillForReconciliation[];
  unmatchedBankTransactions: BankTransaction[];
  warnings: string[];
  summary: {
    totalPendingBills: number;
    totalCreditTransactions: number;
    totalMatched: number;
    totalMatchedAmount: number;
  };
}

export const getReconciliationPendingPayments = async (
  societyId: number,
  startDate?: string,
  endDate?: string
): Promise<{ success: boolean; data: PendingPaymentForReconciliation[] }> => {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();
  return apiRequest(`/reconciliation/pending-bills/${societyId}${query ? `?${query}` : ''}`);
};

export const parseStatementPdf = async (
  file: File,
  societyId?: number
): Promise<{ success: boolean; data: { creditTransactions: BankTransaction[]; transactions: BankTransaction[]; totalCredits: number; totalDebits: number } }> => {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);
  if (societyId) formData.append('societyId', societyId.toString());

  const response = await fetch(`${API_BASE_URL}/reconciliation/parse-statement`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 401) { removeToken(); window.location.href = getLoginPathForRedirect(); throw new Error('Unauthorized'); }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
};

export const runReconciliation = async (
  societyId: number,
  startDate: string | undefined,
  endDate: string | undefined,
  file: File
): Promise<{ success: boolean; data: ReconciliationResult }> => {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);

  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const query = params.toString();

  const response = await fetch(
    `${API_BASE_URL}/reconciliation/reconcile/${societyId}${query ? `?${query}` : ''}`,
    { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData }
  );

  if (!response.ok) {
    if (response.status === 401) { removeToken(); window.location.href = getLoginPathForRedirect(); throw new Error('Unauthorized'); }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
};

export const approveBillPayment = async (billId: number): Promise<{ success: boolean; data: any }> => {
  return apiRequest(`/reconciliation/approve/${billId}`, { method: 'POST' });
};

export const rejectBillPayment = async (billId: number): Promise<{ success: boolean; data: any }> => {
  return apiRequest(`/society-bills/${billId}/reject`, { method: 'POST' });
};

export const batchApproveBills = async (billIds: number[]): Promise<{ success: boolean; data: { approved: number[]; failed: Array<{ billId: number; error: string }> } }> => {
  return apiRequest('/reconciliation/batch-approve', {
    method: 'POST',
    body: JSON.stringify({ billIds }),
  });
};

export const saveBankAccountSummary = async (
  societyId: number,
  payload: {
    sourceFileName: string;
    bankAccountsSummary: Array<{
      date: string;
      particulars: string;
      credit: number | null;
      debit: number | null;
      paymentMode: string | null;
      transactionRefs: string[];
    }>;
  }
): Promise<{ success: boolean; data: any }> => {
  return apiRequest(`/reconciliation/bank-account-summary/${societyId}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export interface BankAccountSummaryEntry {
  id: number;
  societyId: number;
  sourceFileName: string;
  transactionDate: string;
  particulars: string;
  credit: number | null;
  debit: number | null;
  paymentMode: string | null;
  transactionRefs: string[];
  createdAt: string;
  linkedPayment?: {
    paymentId: number;
    paymentIds: number[];
    transactionId: string | null;
    flatNumber: string;
    blockName: string | null;
    amount: number;
    totalAmount: number;
    residentName: string | null;
    status: string;
    billType: string | null;
    screenshotFilePath: string | null;
  } | null;
  linkedBill?: {
    billId: number;
    flatNumber: string;
    blockName: string | null;
    amount: number;
    billingDate?: string;
    residentName: string | null;
    status: string;
    billType: string | null;
    remarks: string | null;
    screenshotFilePath?: string | null;
  } | null;
  isIgnored?: boolean;
  isSettled?: boolean;
  bankSummaryId?: number | null;
  title?: string;
}

export interface BankAccountSummaryResponse {
  entries: BankAccountSummaryEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  totalCredits: number;
  totalDebits: number;
}

export const getBankAccountSummary = async (
  societyId: number,
  page: number = 1,
  limit: number = 100
): Promise<{ success: boolean; data: BankAccountSummaryResponse }> => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  return apiRequest(`/reconciliation/bank-account-summary/${societyId}?${params.toString()}`);
};

export interface ExtractedTransactionInfo {
  transactionId: string | null;
  utr: string | null;
  allRefs: string[];
  confidence: number;
  rawText: string;
}

export const extractTransactionIdFromScreenshot = async (
  file: File
): Promise<{ success: boolean; data: ExtractedTransactionInfo; message: string }> => {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/reconciliation/extract-transaction-id`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 401) { removeToken(); window.location.href = getLoginPathForRedirect(); throw new Error('Unauthorized'); }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
};

// Stat pricing (admin): per-customer, per-stat-parameter price
export interface CustomerStatPrice {
  id: number;
  customer_id: number;
  stat_parameter: string;
  price_per_unit: number;
  created_at: string;
  updated_at: string;
}

export const getStatPriceParameters = async (): Promise<{ success: boolean; data: string[] }> => {
  return apiRequest('/customers/stat-price-parameters');
};

export const getCustomerStatPrices = async (customerId: number): Promise<{ success: boolean; data: CustomerStatPrice[] }> => {
  return apiRequest(`/customers/${customerId}/stat-prices`);
};

export const upsertCustomerStatPrice = async (
  customerId: number,
  stat_parameter: string,
  price_per_unit: number
): Promise<{ success: boolean; data: CustomerStatPrice }> => {
  return apiRequest(`/customers/${customerId}/stat-prices`, {
    method: 'PUT',
    body: JSON.stringify({ stat_parameter, price_per_unit }),
  });
};

export const deleteCustomerStatPrice = async (
  customerId: number,
  stat_parameter: string
): Promise<{ success: boolean }> => {
  return apiRequest(`/customers/${customerId}/stat-prices?stat_parameter=${encodeURIComponent(stat_parameter)}`, {
    method: 'DELETE',
  });
};

// ==================== SOCIETY SETTINGS ====================

export const getSocietySettings = async (
  societyId: number
): Promise<{ success: boolean; data: Record<string, string> }> => {
  return apiRequest(`/society-settings/${societyId}`);
};

export const updateSocietySetting = async (
  societyId: number,
  key: string,
  value: string
): Promise<{ success: boolean; data: Record<string, string>; message: string }> => {
  return apiRequest(`/society-settings/${societyId}/${key}`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  });
};

export const resetSocietySetting = async (
  societyId: number,
  key: string
): Promise<{ success: boolean; data: Record<string, string>; message: string }> => {
  return apiRequest(`/society-settings/${societyId}/${key}`, {
    method: 'DELETE',
  });
};

// ==================== FLAT MEMBERS ====================

export interface FlatMemberInfo {
  userFlatId: number;
  userId: number;
  name: string;
  phone: string;
  email: string;
  gender?: string;
  profilePhoto?: string;
  roleInFlat: string;
  linkedToUserId: number | null;
  isPrimaryTenant: boolean;
  isApproved: boolean;
  isActive: boolean;
  joinedAt: string;
}

export interface RemovalCheckData {
  userId: number;
  name: string;
  phone: string;
  roleInFlat: string;
  flatNumber: string;
  pendingBills: Array<{ id: number; billing_date: string; amount: number; bill_type: string }>;
  pendingBillCount: number;
  pendingBillTotal: number;
  cascadeMembers: Array<{ userFlatId: number; userId: number; name: string; roleInFlat: string }>;
  cascadeMemberCount: number;
}

export const getFlatMembers = async (
  flatId: number
): Promise<{ success: boolean; data: FlatMemberInfo[] }> => {
  return apiRequest(`/flat-members/${flatId}`);
};

export const flatMemberRemovalCheck = async (
  flatId: number,
  userId: number
): Promise<{ success: boolean; data: RemovalCheckData }> => {
  return apiRequest(`/flat-members/${flatId}/by-user/${userId}/removal-check`);
};

export const removeFlatMember = async (
  flatId: number,
  userId: number,
  reason?: string
): Promise<{ success: boolean; data: { removedCount: number; cascadeCount: number }; message: string }> => {
  return apiRequest(`/flat-members/${flatId}/by-user/${userId}`, {
    method: 'DELETE',
    body: JSON.stringify({ reason }),
  });
};

export type FlatMemberRole = 'owner' | 'co_owner' | 'tenant' | 'family_member';

export interface AdminAddFlatMemberPayload {
  phone: string;
  name: string;
  roleInFlat: FlatMemberRole;
  linkedToUserId?: number | null;
  gender?: string | null;
}

export const adminAddFlatMember = async (
  flatId: number,
  payload: AdminAddFlatMemberPayload
): Promise<{
  success: boolean;
  data: FlatMemberInfo;
  userCreated: boolean;
  reactivated: boolean;
  message: string;
}> => {
  return apiRequest(`/flat-members/${flatId}/admin/add`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const unlinkBill = async (ledgerEntryId: number): Promise<{ success: boolean }> => {
  return apiRequest(`/reconciliation/unlink/${ledgerEntryId}`, { method: 'POST' });
};

export const getReconciliationPendingBills = getReconciliationPendingPayments;

export const linkPaymentToBankLine = async (
  bankSummaryId: number,
  billPaymentId: number,
): Promise<{ success: boolean }> => {
  return apiRequest(`/reconciliation/link/${bankSummaryId}/${billPaymentId}`, { method: 'POST' });
};

/** @deprecated Use linkPaymentToBankLine — second arg is billPaymentId. */
export const linkBillToTransaction = linkPaymentToBankLine;

export const ignoreTransaction = async (ledgerEntryId: number, ignore: boolean): Promise<{ success: boolean }> => {
  return apiRequest(`/reconciliation/ignore/${ledgerEntryId}`, { 
    method: 'POST',
    body: JSON.stringify({ ignore })
  });
};

export const markPeriodAsSettled = async (societyId: number, endDate: string): Promise<{ success: boolean }> => {
  return apiRequest(`/reconciliation/settle/${societyId}`, { 
    method: 'POST',
    body: JSON.stringify({ endDate })
  });
};


export const updateTransactionTitle = async (ledgerEntryId: number, title: string): Promise<{ success: boolean }> => {
  return apiRequest(`/reconciliation/update-title/${ledgerEntryId}`, { 
    method: 'POST',
    body: JSON.stringify({ title })
  });
};

export const markStatementAsSettled = async (
  societyId: number,
  fileName: string,
  uploadedAt: string
): Promise<{ success: boolean }> => {
  return apiRequest(`/reconciliation/settle-statement/${societyId}`, {
    method: 'POST',
    body: JSON.stringify({ fileName, uploadedAt }),
  });
};

export interface CreateManualTransactionRequest {
  societyId: number;
  name: string;
  amount: number;
  date: string;
  details?: string;
  documents?: Array<{ document: string; file_name: string }>;
  type: 1 | 2; // 1=Received, 2=Paid
}

export const createSocietyTransaction = async (data: CreateManualTransactionRequest): Promise<{ success: boolean; data?: any }> => {
  return apiRequest('/society-transactions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const deleteSocietyTransaction = async (transactionId: number): Promise<{ success: boolean }> => {
  return apiRequest(`/society-transactions/${transactionId}`, {
    method: 'DELETE',
  });
};

export const bulkIgnoreTransactions = async (transactionIds: number[]): Promise<{ success: boolean }> => {
  return apiRequest('/reconciliation/bulk-ignore', {
    method: 'POST',
    body: JSON.stringify({ transactionIds })
  });
};

// ─── Statement Parser APIs ───

export const extractColumnsFromPdf = async (
  file: File
): Promise<{ success: boolean; data: { rawText: string; detectedHeaders: string[]; sampleRows: { raw: string; cells: string[] }[]; totalLines: number } }> => {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/statement-parsers/extract-columns`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 401) { removeToken(); window.location.href = getLoginPathForRedirect(); throw new Error('Unauthorized'); }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
};

export const generateAndTestParser = async (
  columnMapping: any,
  rawText: string
): Promise<{ success: boolean; data: { generatedCode: string; testResult: { transactions: any[]; creditTransactions: any[]; totalCredits: number; totalDebits: number; transactionCount: number; creditCount: number } } }> => {
  return apiRequest('/statement-parsers/generate-and-test', {
    method: 'POST',
    body: JSON.stringify({ columnMapping, rawText }),
  });
};

export const testParserCode = async (
  parserCode: string,
  rawText: string
): Promise<{ success: boolean; data: { testResult: { transactions: any[]; creditTransactions: any[]; totalCredits: number; totalDebits: number; transactionCount: number; creditCount: number } } }> => {
  return apiRequest('/statement-parsers/test', {
    method: 'POST',
    body: JSON.stringify({ parserCode, rawText }),
  });
};

export const getStatementParsers = async (): Promise<{ success: boolean; data: any[] }> => {
  return apiRequest('/statement-parsers');
};

export const getApprovedParsers = async (): Promise<{ success: boolean; data: { id: number; name: string; description: string | null; status: string }[] }> => {
  return apiRequest('/statement-parsers/approved');
};

export const getStatementParser = async (id: number): Promise<{ success: boolean; data: any }> => {
  return apiRequest(`/statement-parsers/${id}`);
};

export const createStatementParser = async (data: {
  name: string;
  description?: string;
  columnMapping: any;
  parserCode: string;
  sampleFileName?: string;
  sampleRawText?: string;
  status?: string;
}): Promise<{ success: boolean; data: any }> => {
  return apiRequest('/statement-parsers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateStatementParser = async (id: number, data: {
  name?: string;
  description?: string;
  columnMapping?: any;
  parserCode?: string;
  sampleFileName?: string;
  sampleRawText?: string;
  status?: string;
}): Promise<{ success: boolean; data: any }> => {
  return apiRequest(`/statement-parsers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteStatementParser = async (id: number): Promise<{ success: boolean }> => {
  return apiRequest(`/statement-parsers/${id}`, {
    method: 'DELETE',
  });
};

// ——— Flat billing ledger ———

export interface BillCategoryItem {
  id: number;
  society_id: number;
  bill_type_key: string;
  name: string;
  is_active: number;
}

export const getBillCategories = async (
  societyId: number
): Promise<{ success: boolean; data: BillCategoryItem[] }> => {
  return apiRequest(`/bill-categories/${societyId}`);
};

export interface CategoryLedgerBalance {
  bill_type: string;
  principal_outstanding: number;
  interest_outstanding: number;
  advance_balance: number;
  interest_exempt_principal: number;
  total_due: number;
  oldest_unpaid_due_date: string | null;
}

export const getFlatLedgerBalances = async (
  flatId: number,
  societyId: number
): Promise<{ success: boolean; data: CategoryLedgerBalance[] }> => {
  return apiRequest(`/flat-billing-ledger/flat/${flatId}/balances?societyId=${societyId}`);
};

export interface SocietyFlatLedgerBalances {
  flat_id: number;
  balances: CategoryLedgerBalance[];
}

export const getSocietyLedgerBalances = async (
  societyId: number
): Promise<{ success: boolean; data: SocietyFlatLedgerBalances[] }> => {
  return apiRequest(`/flat-billing-ledger/society/${societyId}/balances`);
};

export const setFlatOpeningBalance = async (
  flatId: number,
  body: {
    society_id: number;
    bill_type: string;
    principal_outstanding: number;
    interest_outstanding: number;
    due_date?: string | null;
    advance_balance?: number;
    remarks?: string;
  }
): Promise<{ success: boolean; data: unknown; message?: string }> => {
  return apiRequest(`/flat-billing-ledger/flat/${flatId}/opening-balance`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const rebuildFlatLedger = async (
  flatId: number,
  societyId: number,
  billType: string
): Promise<{ success: boolean; message?: string }> => {
  return apiRequest(`/flat-billing-ledger/flat/${flatId}/rebuild`, {
    method: 'POST',
    body: JSON.stringify({ society_id: societyId, bill_type: billType }),
  });
};

export interface PaymentCategorySlice {
  payment_id: number;
  bill_type: string;
  category_display_name: string;
  amount_paid: number;
  principal_applied: number;
  interest_applied: number;
  advance_applied: number;
  interest_waivable_max: number;
}

export interface PendingPaymentSubmission {
  payment_id: number;
  paid_at: string;
  amount: number;
  bill_type: string;
  category_display_name: string;
  payment_mode: string;
  transaction_id: string | null;
}

export interface PendingPaymentApprovalGroup {
  group_key: string;
  payment_ids: number[];
  bank_summary_id?: number | null;
  bank_matched?: boolean;
  pending_submission_count?: number;
  pending_submissions?: PendingPaymentSubmission[];
  transaction_refs?: string[];
  flat_id: number;
  flat_label: string;
  block_name: string | null;
  flat_number: string | null;
  society_id: number;
  total_amount_paid: number;
  payment_mode: string;
  transaction_id: string | null;
  transaction_group_id: string | null;
  screenshot_file_path: string | null;
  paid_at: string;
  remarks: string | null;
  flat_summary: {
    total_outstanding: number;
    total_principal: number;
    total_interest: number;
    total_advance: number;
  };
  categories: Array<
    CategoryLedgerBalance & {
      category_display_name?: string;
      interest_configuration_label?: string;
    }
  >;
  payment_by_category: PaymentCategorySlice[];
  total_interest_waivable_max: number;
}

export const getEnrichedPendingBillPayments = async (
  societyId: number
): Promise<{ success: boolean; data: PendingPaymentApprovalGroup[] }> => {
  return apiRequest(`/bill-payments/pending/${societyId}/enriched`);
};

export const approveBillPaymentsBatch = async (
  paymentIds: number[],
  interestWaivedByCategory: Record<string, number> = {}
): Promise<{ success: boolean; data: unknown; message?: string }> => {
  return apiRequest('/bill-payments/approve-batch', {
    method: 'POST',
    body: JSON.stringify({
      payment_ids: paymentIds,
      interest_waived_by_category: interestWaivedByCategory,
    }),
  });
};

export const rejectBillPaymentsBatch = async (
  paymentIds: number[]
): Promise<{ success: boolean; data: unknown; message?: string }> => {
  return apiRequest('/bill-payments/reject-batch', {
    method: 'POST',
    body: JSON.stringify({ payment_ids: paymentIds }),
  });
};

/** @deprecated Use approveBillPaymentsBatch */
export const approveBillPaymentRecord = async (
  paymentId: number,
  interestWaivedAmount: number = 0,
  billType: string = 'maintenance'
): Promise<{ success: boolean; data: unknown; message?: string }> => {
  const waived =
    interestWaivedAmount > 0 ? { [billType]: interestWaivedAmount } : {};
  return approveBillPaymentsBatch([paymentId], waived);
};

/** @deprecated Use rejectBillPaymentsBatch */
export const rejectBillPaymentRecord = async (
  paymentId: number
): Promise<{ success: boolean; data: unknown; message?: string }> => {
  return rejectBillPaymentsBatch([paymentId]);
};

// ==================== GUARDS ====================

/** Raw guard row from GET /api/guards */
export interface GuardApiRow {
  id: number;
  user_id: number;
  user_name?: string;
  user_phone?: string;
  user_email?: string | null;
  name?: string;
  phone?: string;
  email?: string | null;
  society_id?: number;
  created_at?: string;
}

export interface Guard {
  /** user_roles.id — use for list keys */
  id: number;
  user_id: number;
  name: string;
  phone: string;
  email?: string | null;
  society_id?: number;
  created_at?: string;
}

export function normalizeGuard(row: GuardApiRow): Guard {
  return {
    id: row.id,
    user_id: row.user_id,
    name: (row.user_name ?? row.name ?? '').trim(),
    phone: (row.user_phone ?? row.phone ?? '').trim(),
    email: row.user_email ?? row.email ?? null,
    society_id: row.society_id,
    created_at: row.created_at,
  };
}

export interface GuardPasswordRequest {
  id: number;
  guard_id: number;
  guard_name?: string;
  guard_phone?: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  reviewed_at?: string | null;
}

export const getGuards = async (
  societyId: number
): Promise<{ success: boolean; data: Guard[] }> => {
  const res = await apiRequest<{ success: boolean; data: GuardApiRow[] }>(
    `/guards?societyId=${societyId}`
  );
  if (res.success && Array.isArray(res.data)) {
    return { ...res, data: res.data.map(normalizeGuard) };
  }
  return res as { success: boolean; data: Guard[] };
};

export const addGuard = async (
  data: { name: string; phone: string; societyId: number }
): Promise<{ success: boolean; data: Guard; message?: string }> => {
  return apiRequest('/guards', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/** guardUserId = users.id (backend DELETE /guards/:guardId expects user id, not user_roles.id) */
export const removeGuard = async (
  guardUserId: number,
  societyId: number
): Promise<{ success: boolean; message?: string }> => {
  return apiRequest(`/guards/${guardUserId}?societyId=${societyId}`, { method: 'DELETE' });
};

export const getGuardPasswordRequests = async (
  societyId: number
): Promise<{ success: boolean; data: GuardPasswordRequest[] }> => {
  return apiRequest(`/guards/password-requests?societyId=${societyId}`);
};

export const approveGuardPasswordRequest = async (
  requestId: number
): Promise<{ success: boolean; message?: string }> => {
  return apiRequest(`/guards/password-requests/${requestId}/approve`, { method: 'POST' });
};

export const rejectGuardPasswordRequest = async (
  requestId: number
): Promise<{ success: boolean; message?: string }> => {
  return apiRequest(`/guards/password-requests/${requestId}/reject`, { method: 'POST' });
};

// ==================== PENDING MEMBERSHIP REQUESTS ====================

export interface PendingUser {
  user_id: number;
  user_name: string;
  user_phone: string;
  user_email?: string | null;
  society_id: number;
  isUserSocietyApproved: number;
  requested_date: string;
  flat_id?: number;
  flat_number?: string;
  area?: number;
  block_name?: string | null;
  is_primary?: number;
  role_in_flat?: string | null;
}

export interface PendingBill {
  bill_id: number;
  flat_id: number;
  flat_number?: string;
  block_name?: string | null;
  amount: number;
  billing_date: string;
  bill_type: string;
  status: string;
  remarks?: string | null;
  created_on: string;
}

export interface PendingUsersData {
  pendingUsers: PendingUser[];
  pendingBills: PendingBill[];
}

export const getPendingUsers = async (
  societyId: number
): Promise<{ success: boolean; data: PendingUsersData }> => {
  return apiRequest(`/users/pending/society/${societyId}`);
};

export const approveUser = async (
  userId: number,
  options?: { area?: number }
): Promise<{ success: boolean; message?: string; error?: string }> => {
  const body =
    options?.area !== undefined && options.area > 0
      ? JSON.stringify({ area: options.area })
      : undefined;
  return apiRequest(`/users/${userId}/approve`, {
    method: 'POST',
    body,
  });
};

export const rejectUser = async (
  userId: number,
  societyId: number
): Promise<{ success: boolean; message?: string; error?: string }> => {
  return apiRequest(`/users/${userId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ societyId }),
  });
};

// ==================== COMMITTEE / USER ROLES ====================

export interface SocietyRole {
  id: number;
  name: string;
  description?: string | null;
}

export interface CommitteeMember {
  id: number;
  user_id: number;
  role_id: number;
  society_id: number;
  user_name: string;
  user_phone?: string;
  user_email?: string | null;
  role_name: string;
  flat_id?: number;
  flat_number?: string;
  block_name?: string | null;
  role_in_flat?: string | null;
}

export interface NonCommitteeResident {
  user_id: number;
  user_name: string;
  user_phone?: string;
  user_email?: string | null;
  flat_id?: number;
  flat_number?: string;
  block_name?: string | null;
  role_in_flat?: string | null;
}

export const getCommitteeMembers = async (
  societyId: number
): Promise<{ success: boolean; data: CommitteeMember[] }> => {
  return apiRequest(`/user-roles/committee/society/${societyId}`);
};

export const getNonCommitteeFlatOwners = async (
  societyId: number
): Promise<{ success: boolean; data: NonCommitteeResident[] }> => {
  return apiRequest(`/user-roles/non-committee/society/${societyId}`);
};

export const getAllRoles = async (): Promise<{ success: boolean; data: SocietyRole[] }> => {
  return apiRequest('/roles');
};

export const assignRoleToUser = async (data: {
  userId: number;
  roleId: number;
  societyId: number;
}): Promise<{ success: boolean; message?: string }> => {
  return apiRequest('/user-roles', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateUserRoleInSociety = async (
  userId: number,
  societyId: number,
  roleId: number
): Promise<{ success: boolean; message?: string }> => {
  return apiRequest(`/user-roles/user/${userId}/society/${societyId}`, {
    method: 'PUT',
    body: JSON.stringify({ roleId }),
  });
};

export const removeUserRoleFromSociety = async (
  userId: number,
  societyId: number
): Promise<{ success: boolean; message?: string }> => {
  return apiRequest(`/user-roles/user/${userId}/society/${societyId}`, {
    method: 'DELETE',
  });
};

// ==================== GUESTS / VISITORS ====================

export interface GuestCheckIn {
  id: number;
  flat_id: number;
  flat_number?: string;
  block_name?: string;
  guest_name: string;
  guest_phone?: string | null;
  purpose?: string | null;
  vehicle_number?: string | null;
  check_in_time: string;
  check_out_time?: string | null;
  approved_by?: string | null;
  status?: string;
  guard_name?: string | null;
  photo_url?: string | null;
  visitor_photo_path?: string | null;
  vehicle_photo_path?: string | null;
}

export const getGuestCheckIns = async (
  societyId: number,
  flatId?: number,
  startDate?: string,
  endDate?: string
): Promise<{ success: boolean; data: GuestCheckIn[] }> => {
  return apiRequest('/guest/get-check-in', {
    method: 'POST',
    body: JSON.stringify({ societyId, flatId, startDate, endDate }),
  });
};

// ==================== VEHICLES ====================

export interface Vehicle {
  id: number;
  flat_id: number | null;
  flat_number?: string;
  block_name?: string;
  vehicle_number: string;
  vehicle_type?: string | null;
  make?: string | null;
  model?: string | null;
  color?: string | null;
  owner_name?: string | null;
  created_at?: string;
}

export const getVehicles = async (
  societyId: number
): Promise<{ success: boolean; data: Vehicle[] }> => {
  return apiRequest(`/vehicles?societyId=${societyId}`);
};

export const getVehiclesByFlat = async (
  flatId: number
): Promise<{ success: boolean; data: Vehicle[] }> => {
  return apiRequest(`/vehicles/flat/${flatId}`);
};

// ==================== BLOCKS ====================

export interface Block {
  id: number;
  society_id: number;
  name: string;
  created_at?: string;
}

export const getBlocks = async (
  societyId: number
): Promise<{ success: boolean; data: Block[] }> => {
  return apiRequest(`/blocks?societyId=${societyId}`);
};

// ==================== SOCIETY UPDATE ====================

export const updateSociety = async (
  societyId: number,
  data: Partial<{ name: string; location: string; city: string; state: string; pincode: string; country: string; type: string }>
): Promise<{ success: boolean; data: any; message?: string }> => {
  return apiRequest(`/societies/${societyId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/** Matches backend `LedgerStatementLine` from flat billing statement API. */
export interface LedgerStatementLine {
  id: number;
  entry_type: string;
  status: string;
  bill_type: string;
  transaction_date: string;
  due_date: string | null;
  bill_billing_date?: string | null;
  previous_principal: number;
  principal_delta: number;
  principal_after: number;
  previous_interest: number;
  interest_delta: number;
  interest_after: number;
  advance_delta: number;
  advance_after: number;
  entry_side: 'debit' | 'credit' | 'neutral';
  transaction_amount: number;
  total_due_before: number;
  total_due_after: number;
  interest_waived: number;
  remarks: string | null;
  reference_type: string | null;
  reference_id: number | null;
  bill_title?: string | null;
  bill_description?: string | null;
}

export const getFlatLedgerStatement = async (
  flatId: number,
  societyId: number,
  opts?: { dateFrom?: string; dateTo?: string; billType?: string }
): Promise<{ success: boolean; data: { lines: LedgerStatementLine[]; balances: CategoryLedgerBalance[] } }> => {
  const params = new URLSearchParams();
  params.set('societyId', String(societyId));
  if (opts?.dateFrom) params.set('dateFrom', toUtcDateOnlyForApi(opts.dateFrom));
  if (opts?.dateTo) params.set('dateTo', toUtcDateOnlyForApi(opts.dateTo));
  if (opts?.billType) params.set('billType', opts.billType);
  const query = params.toString();
  return apiRequest(`/flat-billing-ledger/flat/${flatId}/statement?${query}`);
};

/** Row from GET /society-bills/flat/:flatId */
export interface SocietyBillRow {
  id: number;
  society_id: number;
  flat_id: number;
  billing_date: string;
  amount: number;
  bill_type: string;
  status: string;
  recurring_event_id: number | null;
  recurring_job_run_id: number | null;
  recurring_bill_template_id: number | null;
  adjusted_bill_id: number | null;
  remarks: string | null;
  title: string | null;
  due_date: string | null;
  amount_paid: number;
  interest_amount: number;
  paid_by_user_id?: number | null;
  paid_at?: string | null;
  created_on: string;
}

export const getSocietyBillsByFlatId = async (
  flatId: number
): Promise<{ success: boolean; data: SocietyBillRow[] }> => {
  return apiRequest(`/society-bills/flat/${flatId}`);
};

export interface CreateSocietyBillRequest {
  society_id: number;
  requestType: string;
  bill_type: string;
  title: string;
  description: string;
  flats: number[];
  amount: number;
  amount_per_sqft?: boolean;
  billDate: string;
  due_days?: number;
  issued_at?: string;
}

export const createSocietyBills = async (
  data: CreateSocietyBillRequest
): Promise<{ success: boolean; data: SocietyBillRow[]; message: string }> => {
  return apiRequest('/society-bills', {
    method: 'POST',
    body: JSON.stringify({
      ...data,
      billDate: toUtcDateOnlyForApi(data.billDate),
      issued_at: data.issued_at ?? toUtcIsoInstantForApi(),
    }),
  });
};

/** Bills report PDF/XLSX from server (society-wide, date range). */
export const downloadSocietyBillsReportFile = async (
  societyId: number,
  startDate: string,
  endDate: string,
  format: 'pdf' | 'excel'
): Promise<void> => {
  const token = getToken();
  if (!token) {
    throw new Error('Unauthorized');
  }
  const params = new URLSearchParams({
    startDate: toUtcDateOnlyForApi(startDate),
    endDate: toUtcDateOnlyForApi(endDate),
    type: format,
  });
  const response = await fetch(
    `${API_BASE_URL}/society-bills/society/${societyId}/report?${params}`,
    { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) {
    if (response.status === 401) {
      removeToken();
      window.location.href = getLoginPathForRedirect();
      throw new Error('Unauthorized');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  const blob = await response.blob();
  const ext = format === 'pdf' ? 'pdf' : 'xlsx';
  const filename = `society-bills-report-${startDate}_to_${endDate}.${ext}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/** Society billing audit report PDF/XLSX (balances, aging, pending payments). */
export const downloadSocietyAuditReportFile = async (
  societyId: number,
  format: 'pdf' | 'excel',
  dateFrom?: string,
  dateTo?: string
): Promise<void> => {
  const token = getToken();
  if (!token) {
    throw new Error('Unauthorized');
  }
  const params = new URLSearchParams({ format });
  if (dateFrom) params.set('dateFrom', toUtcDateOnlyForApi(dateFrom));
  if (dateTo) params.set('dateTo', toUtcDateOnlyForApi(dateTo));
  const response = await fetch(
    `${API_BASE_URL}/bill-payments/society/${societyId}/audit-report?${params}`,
    { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) {
    if (response.status === 401) {
      removeToken();
      window.location.href = getLoginPathForRedirect();
      throw new Error('Unauthorized');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  const blob = await response.blob();
  const ext = format === 'pdf' ? 'pdf' : 'xlsx';
  const rangeSuffix =
    dateFrom && dateTo ? `-${dateFrom}_to_${dateTo}` : dateFrom ? `-from-${dateFrom}` : '';
  const filename = `society-audit-report-${societyId}${rangeSuffix}.${ext}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export interface MaintenanceTrackingData {
  societyId: number;
  societyName: string;
  month: number;
  year: number;
  totalUsers: number;
  paidUsers: number;
  paidNotApprovedUsers: number;
  dueUsers: number;
  notApplicableUsers: number;
  totalMaintenanceAmount: number;
  paidAmount: number;
  pendingAmount: number;
}

export const getMaintenanceTracking = async (
  societyId: number,
  month: number,
  year: number
): Promise<{ success: boolean; data: MaintenanceTrackingData }> => {
  const params = new URLSearchParams({
    month: String(month),
    year: String(year),
  });
  return apiRequest(`/maintenance-tracking/${societyId}?${params}`);
};

export interface BillPaymentRecord {
  id: number;
  flat_id: number;
  society_id: number;
  bill_type: string;
  amount: number;
  payment_mode: string;
  transaction_id: string | null;
  approval_status: string;
  paid_at: string;
  remarks: string | null;
}

export const submitAdminReceivedCategoryPayment = async (data: {
  flat_id: number;
  society_id: number;
  bill_type: string;
  amount: number;
  payment_mode: string;
  transaction_id?: string;
  screenshot_file_id?: string;
  screenshot_file_path?: string;
  remarks?: string;
  paid_at?: string;
}): Promise<{ success: boolean; data: BillPaymentRecord[]; message: string }> => {
  return apiRequest('/bill-payments/admin-receive/category', {
    method: 'POST',
    body: JSON.stringify({
      ...data,
      paid_at: data.paid_at ?? toUtcIsoInstantForApi(),
    }),
  });
};

export const submitAdminReceivedMultiCategoryPayment = async (data: {
  flat_id: number;
  society_id: number;
  allocations: { bill_type: string; amount: number }[];
  payment_mode: string;
  transaction_id?: string;
  screenshot_file_id?: string;
  screenshot_file_path?: string;
  remarks?: string;
  paid_at?: string;
}): Promise<{ success: boolean; data: BillPaymentRecord[]; message: string }> => {
  return apiRequest('/bill-payments/admin-receive/category-multi', {
    method: 'POST',
    body: JSON.stringify({
      ...data,
      paid_at: data.paid_at ?? toUtcIsoInstantForApi(),
    }),
  });
};

export interface ScheduleBill {
  id: number;
  template_id: number;
  event_id: number;
  society_id: number;
  title: string;
  description: string | null;
  amount: number;
  amount_per_sqft?: boolean;
  bill_type: string;
  frequency: string;
  interval_value: number;
  days_of_week: string | null;
  day_of_month: number | null;
  start_date: string;
  end_date: string | null;
  applies_to_occupied_only: boolean;
  is_active: boolean;
  exemption_flats: number[];
  created_on: string;
  created_by: string | null;
  modified_on: string | null;
  modified_by: string | null;
}

export const getScheduledBills = async (
  societyId: number
): Promise<{ success: boolean; data: ScheduleBill[]; message?: string }> => {
  return apiRequest(`/schedule-bills/society/${societyId}`);
};

// ==================== ADMIN ONBOARD ====================

export interface OnboardBlock { blockName: string; numberOfFloors?: number; customLetter?: string; }
export interface OnboardFlat {
  block: string;
  flat_number: string;
  floor?: number;
  area?: number;
  flat_type?: string;
  user_name?: string;
  user_phone?: string;
  user_email?: string;
  role_in_flat?: 'owner' | 'tenant';
  society_role?: string;   // e.g. president | treasurer | body_member | normal_member | guard
  two_wheelers?: string;   // comma-separated vehicle numbers e.g. "MH01AB1234,MH01AB5678"
  four_wheelers?: string;  // comma-separated vehicle numbers e.g. "MH02CD9999"
}
export interface OnboardRequest {
  society: {
    name: string;
    location: string;
    city: string;
    state: string;
    pincode: string;
    country?: string;
    type?: string;
    flatNoFormat?: string;
    latlong?: string;
    account_number?: string;
    account_ifsc?: string;
  };
  blocks: OnboardBlock[];
  flats: OnboardFlat[];
}
export interface OnboardResult {
  societyId: number;
  societyPasscode: string;
  blocksCreated: number;
  flatsCreated: number;
  usersCreated: number;
  usersLinked: number;
  vehiclesCreated: number;
  rolesAssigned: number;
  warnings: string[];
}

export const adminOnboardSociety = async (
  data: OnboardRequest
): Promise<{ success: boolean; data: OnboardResult; message?: string }> => {
  return apiRequest('/admin/onboard-society', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export interface OnboardExportPayload {
  society: {
    name: string;
    location: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
    type: string;
    flatNoFormat: string;
    latlong: string;
    account_number: string;
    account_ifsc: string;
  };
  blocks: Array<{ blockName: string; numberOfFloors: number; customLetter: string }>;
  flats: Array<{
    block: string;
    flat_number: string;
    floor: number | string;
    area?: number;
    user_name: string;
    user_phone: string;
    user_email: string;
    role_in_flat: string;
    society_role: string;
    two_wheelers: string;
    four_wheelers: string;
  }>;
}

export const adminExportSocietyOnboard = async (
  societyId: number
): Promise<{ success: boolean; data: OnboardExportPayload; message?: string }> => {
  return apiRequest(`/admin/societies/${societyId}/export-onboard`);
};

/** Society admin or website admin: export all flat members (owner, tenant, family) in bulk-upload format */
export const societyAdminExportMembers = async (
  societyId: number
): Promise<{ success: boolean; data: OnboardExportPayload; message?: string }> => {
  return apiRequest(`/societies/admin/${societyId}/export-onboard`);
};

export const adminDeleteSociety = async (
  societyId: number
): Promise<{ success: boolean; message?: string }> => {
  return apiRequest(`/societies/${societyId}`, { method: 'DELETE' });
};

// ==================== COMPLAINTS ====================

export type ComplaintStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type ComplaintCategory =
  | 'plumbing'
  | 'electrical'
  | 'carpentry'
  | 'housekeeping'
  | 'security'
  | 'parking'
  | 'noise'
  | 'common_area'
  | 'elevator'
  | 'water_supply'
  | 'other';
export type ComplaintPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface Complaint {
  id: number;
  society_id: number;
  user_id: number;
  flat_id: number | null;
  title: string;
  description: string;
  category: ComplaintCategory;
  priority: ComplaintPriority;
  status: ComplaintStatus;
  admin_remarks: string | null;
  resolved_at: string | null;
  resolved_by: number | null;
  created_at: string;
  updated_at: string;
  /** Joined fields */
  user_name?: string | null;
  user_phone?: string | null;
  flat_number?: string | null;
  block_name?: string | null;
  user_role?: string | null;
}

export interface ComplaintMessage {
  id: number;
  complaint_id: number;
  user_id: number;
  author_role: 'admin' | 'resident';
  body: string;
  created_at: string;
  user_name?: string | null;
}

export const getComplaintsBySociety = async (
  societyId: number,
  opts?: { status?: ComplaintStatus; category?: ComplaintCategory }
): Promise<{ success: boolean; data: Complaint[] }> => {
  const params = new URLSearchParams();
  if (opts?.status) params.set('status', opts.status);
  if (opts?.category) params.set('category', opts.category);
  const qs = params.toString();
  return apiRequest(`/complaints/society/${societyId}${qs ? `?${qs}` : ''}`);
};

export const getComplaintById = async (
  complaintId: number
): Promise<{ success: boolean; data: Complaint }> => {
  return apiRequest(`/complaints/${complaintId}`);
};

export const updateComplaintStatus = async (
  complaintId: number,
  status: ComplaintStatus,
  adminRemarks?: string
): Promise<{ success: boolean; data: Complaint; message?: string }> => {
  return apiRequest(`/complaints/${complaintId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, admin_remarks: adminRemarks }),
  });
};

export const getComplaintMessages = async (
  complaintId: number
): Promise<{ success: boolean; data: ComplaintMessage[] }> => {
  return apiRequest(`/complaints/${complaintId}/messages`);
};

export const postComplaintMessage = async (
  complaintId: number,
  body: string
): Promise<{ success: boolean; data: ComplaintMessage }> => {
  return apiRequest(`/complaints/${complaintId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
};

// App version / force update
export type AppPlatform = 'ios' | 'android';

export interface AppVersion {
  id: number;
  platform: AppPlatform;
  latest_version: string;
  min_supported_version: string;
  force_update: number;
  update_message: string | null;
  store_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface AppVersionRelease {
  id: number;
  platform: AppPlatform;
  version: string;
  is_force_update: number;
  update_message: string | null;
  created_at: string;
}

export const getAppVersions = async (): Promise<{ success: boolean; data: AppVersion[] }> => {
  return apiRequest('/app/versions');
};

export const getAppReleases = async (
  platform: AppPlatform
): Promise<{ success: boolean; data: AppVersionRelease[] }> => {
  return apiRequest(`/app/releases?platform=${platform}`);
};

export const createAppRelease = async (payload: {
  platform: AppPlatform;
  version: string;
  is_force_update: boolean;
  update_message?: string;
}): Promise<{ success: boolean; data: AppVersionRelease; message?: string }> => {
  return apiRequest('/app/releases', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateAppRelease = async (
  platform: AppPlatform,
  version: string,
  payload: { is_force_update: boolean; update_message?: string }
): Promise<{ success: boolean; data: AppVersionRelease; message?: string }> => {
  return apiRequest(`/app/releases/${platform}/${version}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const deleteAppRelease = async (
  platform: AppPlatform,
  version: string
): Promise<{ success: boolean; message?: string }> => {
  return apiRequest(`/app/releases/${platform}/${version}`, {
    method: 'DELETE',
  });
};

export const updateAppVersionConfig = async (
  platform: AppPlatform,
  payload: Partial<{
    latest_version: string;
    min_supported_version: string;
    force_update: boolean;
    update_message: string;
    store_url: string;
  }>
): Promise<{ success: boolean; data: AppVersion; message?: string }> => {
  return apiRequest(`/app/versions/${platform}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

const RESIDENT_TOKEN_KEY = 'resident_auth_token';
const RESIDENT_CTX_KEY = 'resident_user_context';

const getResidentToken = (): string | null => {
  const resident = localStorage.getItem(RESIDENT_TOKEN_KEY);
  if (resident) return resident;
  return getToken();
};

const getResidentContext = (): any | null => {
  const raw = localStorage.getItem(RESIDENT_CTX_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const residentApiRequest = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const token = getResidentToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem(RESIDENT_TOKEN_KEY);
      localStorage.removeItem(RESIDENT_CTX_KEY);
      if (typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify({ action: 'LOGOUT' }));
      }
      throw new Error('Session expired. Please login again.');
    }
    const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
};

export async function verifyResidentSession(): Promise<{ success: boolean; data: any }> {
  return residentApiRequest('/auth/verify', { method: 'GET' });
}

export interface ResidentMarketplaceCategory {
  id: number;
  name: string;
  parent_id: number | null;
  icon: string | null;
  sort_order: number;
}

export interface ResidentMarketplaceItem {
  id: number;
  title: string;
  description: string | null;
  price: number;
  item_condition: 'new' | 'like_new' | 'used';
  item_status?: 'active' | 'sold' | 'expired' | 'deleted';
  category_id?: number;
  sub_category_id?: number | null;
  category_name?: string | null;
  sub_category_name?: string | null;
  is_negotiable?: number;
  trust_tag: 'verified_neighbor' | 'nearby_society';
  images: string[];
  metadata: Record<string, unknown>;
  distance_km?: number | null;
  seller_name?: string | null;
  seller_user_id?: number;
  seller_flat_number?: string | null;
  seller_block_name?: string | null;
  society_name?: string | null;
  created_at?: string;
}

export async function fetchMarketplaceCategories(): Promise<{
  success: boolean;
  data: ResidentMarketplaceCategory[];
}> {
  return residentApiRequest('/marketplace/categories', { method: 'GET' });
}

export async function fetchMarketplaceItems(params?: {
  scope?: 'my_society' | 'nearby' | 'all' | 'my_listings';
  radius_km?: number;
  category_id?: number;
  sub_category_id?: number;
  min_price?: number;
  max_price?: number;
  condition?: 'new' | 'like_new' | 'used';
  search?: string;
  sort_by?: 'newest' | 'price_asc' | 'price_desc' | 'nearest' | 'random';
  include_own?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ success: boolean; data: ResidentMarketplaceItem[]; meta?: any }> {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
  });
  return residentApiRequest(`/marketplace/items?${query.toString()}`, { method: 'GET' });
}

export async function createMarketplaceItem(payload: {
  title: string;
  description?: string;
  price: number;
  category_id: number;
  sub_category_id?: number | null;
  item_condition?: 'new' | 'like_new' | 'used';
  images?: string[];
  is_negotiable?: boolean;
}): Promise<{ success: boolean; data: ResidentMarketplaceItem; message?: string }> {
  return residentApiRequest('/marketplace/items', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateMarketplaceItemStatus(
  itemId: number,
  status: 'active' | 'sold' | 'expired' | 'deleted',
): Promise<{ success: boolean; data: ResidentMarketplaceItem; message?: string }> {
  return residentApiRequest(`/marketplace/items/${itemId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function fetchMyMarketplaceItems(): Promise<{
  success: boolean;
  data: ResidentMarketplaceItem[];
}> {
  return fetchMarketplaceItems({ scope: 'my_listings', limit: 50 });
}

export async function fetchMarketplaceItem(
  itemId: number,
): Promise<{ success: boolean; data: ResidentMarketplaceItem }> {
  return residentApiRequest(`/marketplace/items/${itemId}`, { method: 'GET' });
}

export async function openMarketplaceItemChat(
  itemId: number,
): Promise<{ success: boolean; data: { id: number; item_id?: number } }> {
  return residentApiRequest(`/marketplace/items/${itemId}/chat`, { method: 'POST' });
}

export async function uploadMarketplaceImage(file: File): Promise<UploadDocumentResponse> {
  const token = getResidentToken();
  if (!token) {
    throw new Error('Please sign in again to upload photos');
  }

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/documents/marketplace/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to upload image');
  }

  const payload = (await response.json()) as UploadDocumentResponse;
  if (!payload.success) {
    throw new Error(payload.message || 'Failed to upload image');
  }

  return payload;
}

export async function fetchMarketplaceChats(): Promise<{ success: boolean; data: any[] }> {
  return residentApiRequest('/marketplace/chats', { method: 'GET' });
}

export async function fetchMarketplaceUnreadCount(): Promise<{
  success: boolean;
  data: { unread_count: number };
}> {
  return residentApiRequest('/marketplace/chats/unread-count', { method: 'GET' });
}

export async function fetchMarketplaceChatMessages(chatId: number): Promise<{
  success: boolean;
  data: Array<{
    id: number;
    sender_user_id: number;
    sender_name?: string | null;
    message: string;
    is_read: number;
    is_mine: boolean;
    created_at: string;
  }>;
}> {
  const res = await residentApiRequest<{ success: boolean; data: any[] }>(
    `/marketplace/chats/${chatId}/messages`,
    { method: 'GET' },
  );
  const me = Number(getResidentContext()?.user?.id || 0);
  const enriched = (res.data || []).map((m) => ({ ...m, is_mine: Number(m.sender_user_id) === me }));
  return { ...res, data: enriched };
}

export async function sendMarketplaceChatMessage(
  chatId: number,
  message: string,
): Promise<{ success: boolean; data: any }> {
  return residentApiRequest(`/marketplace/chats/${chatId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

export async function markMarketplaceChatRead(chatId: number): Promise<{ success: boolean; data: any }> {
  return residentApiRequest(`/marketplace/chats/${chatId}/read`, { method: 'POST' });
}

export interface ResidentHomeBootstrap {
  stories: any[];
  spotlight: any[];
  unread_count: number;
  categories_preview: Array<{ id: number; name: string; description: string | null }>;
  category_sections?: Array<{
    category: { id: number; name: string; description: string | null };
    promotions: any[];
  }>;
  marketplace_preview?: ResidentMarketplaceItem[];
  marketplace_count?: number;
}

export async function recordResidentImpression(
  promotionId: number,
  impressionType: 'view' | 'click' | 'story_view' | 'story_complete',
): Promise<{ success: boolean }> {
  return residentApiRequest('/promotions/impressions', {
    method: 'POST',
    body: JSON.stringify({ promotion_id: promotionId, impression_type: impressionType }),
  });
}

export async function fetchResidentHomeBootstrap(societyId?: number | null): Promise<{
  success: boolean;
  data: ResidentHomeBootstrap;
}> {
  const qs = societyId ? `?societyId=${societyId}` : '';
  return residentApiRequest(`/resident-home/bootstrap${qs}`, { method: 'GET' });
}

export async function fetchResidentPromotions(
  type: 'story' | 'spotlight' | 'category',
  societyId?: number | null,
): Promise<{ success: boolean; data: any[] }> {
  const params = new URLSearchParams();
  params.set('type', type);
  if (societyId) params.set('societyId', String(societyId));
  return residentApiRequest(`/promotions?${params.toString()}`, { method: 'GET' });
}
